import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Inbox,
  CheckCircle2,
  Clock,
  Loader2,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar as CalIcon,
  Image as ImageIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listInquiries, updateInquiryStatus, deleteInquiry, getStats } from "@/lib/api";
import GalleryManager from "@/components/admin/GalleryManager";

const STATUS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "booked", label: "Booked" },
  { value: "completed", label: "Completed" },
  { value: "declined", label: "Declined" },
];

const SERVICE_LABEL = {
  real_estate: "Real Estate",
  watersports: "Watersports",
  cinematic: "Cinematic",
  other: "Other",
};

const STATUS_COLOR = {
  new: "text-[#E5A93B] border-[#E5A93B]/40",
  contacted: "text-blue-300 border-blue-300/40",
  booked: "text-emerald-300 border-emerald-300/40",
  completed: "text-white/60 border-white/20",
  declined: "text-red-300 border-red-300/40",
};

export default function Admin() {
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, booked: 0, completed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        listInquiries(filter === "all" ? null : filter),
        getStats(),
      ]);
      setInquiries(list);
      setStats(s);
    } catch (e) {
      toast.error("Could not load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateInquiryStatus(id, status);
      setInquiries((prev) => prev.map((i) => (i.id === id ? updated : i)));
      if (selected?.id === id) setSelected(updated);
      toast.success(`Marked as ${status}`);
      const s = await getStats();
      setStats(s);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await deleteInquiry(id);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted");
      const s = await getStats();
      setStats(s);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#05080F] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" data-testid="admin-home-link" className="flex items-baseline gap-3">
            <span className="font-mono text-xs tracking-[0.3em] text-[#E5A93B]">AQUA / AERIALS</span>
            <span className="font-mono text-xs tracking-[0.3em] text-white/40">/ ADMIN</span>
          </Link>
          <nav className="flex items-center gap-1 border border-white/10">
            <TabBtn
              active={tab === "inquiries"}
              onClick={() => setTab("inquiries")}
              testid="tab-inquiries"
              icon={<Inbox size={13} />}
              label="Inquiries"
            />
            <TabBtn
              active={tab === "gallery"}
              onClick={() => setTab("gallery")}
              testid="tab-gallery"
              icon={<ImageIcon size={13} />}
              label="Gallery"
            />
          </nav>
        </div>
      </header>

      {tab === "gallery" ? (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
          <div className="mb-10">
            <span className="overline">§ Gallery</span>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-2">
              Manage your <span className="text-[#E5A93B]">photos &amp; videos</span>
            </h1>
            <p className="text-white/50 text-sm mt-3 max-w-2xl">
              Anything you add here appears on the public gallery section of the home page. Hide items with the eye icon to keep them off the site without deleting.
            </p>
          </div>
          <GalleryManager />
        </section>
      ) : (
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          <Stat icon={<Inbox size={16} />} label="Total" value={stats.total} testid="stat-total" />
          <Stat icon={<Clock size={16} />} label="New" value={stats.new} testid="stat-new" />
          <Stat icon={<CalIcon size={16} />} label="Booked" value={stats.booked} testid="stat-booked" />
          <Stat icon={<CheckCircle2 size={16} />} label="Completed" value={stats.completed} testid="stat-completed" />
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Inquiries <span className="text-[#E5A93B]">/{inquiries.length}</span>
          </h1>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              data-testid="filter-status"
              className="w-[200px] bg-[#0D1322] border-white/10 text-white rounded-none h-11"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
              <SelectItem value="all" data-testid="filter-all">All statuses</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`filter-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/60 py-20 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : inquiries.length === 0 ? (
          <div
            data-testid="empty-state"
            className="border border-white/10 bg-[#0D1322] py-24 text-center"
          >
            <span className="overline">No inquiries yet</span>
            <p className="mt-3 text-white/50">When customers submit the form, they appear here.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 border border-white/10 bg-[#0D1322]">
              <table className="w-full text-sm">
                <thead className="border-b border-white/10">
                  <tr className="text-left">
                    <Th>Name</Th>
                    <Th>Service</Th>
                    <Th>Date</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((i) => (
                    <tr
                      key={i.id}
                      data-testid={`row-${i.id}`}
                      onClick={() => setSelected(i)}
                      className={`border-b border-white/5 cursor-pointer hover:bg-[#161E33] transition ${
                        selected?.id === i.id ? "bg-[#161E33]" : ""
                      }`}
                    >
                      <Td>
                        <div className="text-white font-medium">{i.name}</div>
                        <div className="font-mono text-[11px] text-white/50">{i.email}</div>
                      </Td>
                      <Td className="text-white/80">{SERVICE_LABEL[i.service_type] || i.service_type}</Td>
                      <Td className="font-mono text-white/70 text-xs">
                        {i.preferred_date || "—"}
                      </Td>
                      <Td>
                        <span
                          className={`inline-flex font-mono text-[10px] uppercase tracking-wider border px-2 py-1 ${STATUS_COLOR[i.status] || ""}`}
                        >
                          {i.status}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:col-span-5">
              {selected ? (
                <div
                  data-testid="inquiry-detail"
                  className="border border-white/10 bg-[#0D1322] p-6 sticky top-24"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <span className="overline text-white/40">Inquiry</span>
                      <h3 className="text-2xl font-bold mt-1">{selected.name}</h3>
                      <div className="font-mono text-[11px] text-white/40 mt-1">
                        {format(new Date(selected.created_at), "PPp")}
                      </div>
                    </div>
                    <button
                      data-testid="delete-inquiry"
                      onClick={() => remove(selected.id)}
                      className="text-white/40 hover:text-red-400 transition"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <DetailRow icon={<Mail size={14} />} label="Email">
                    <a href={`mailto:${selected.email}`} className="hover:text-[#E5A93B] break-all">
                      {selected.email}
                    </a>
                  </DetailRow>
                  {selected.phone && (
                    <DetailRow icon={<Phone size={14} />} label="Phone">
                      {selected.phone}
                    </DetailRow>
                  )}
                  <DetailRow icon={<CalIcon size={14} />} label="Preferred date">
                    {selected.preferred_date || "Not specified"}
                  </DetailRow>
                  {selected.location && (
                    <DetailRow icon={<MapPin size={14} />} label="Location">
                      {selected.location}
                    </DetailRow>
                  )}
                  <DetailRow label="Service">
                    {SERVICE_LABEL[selected.service_type] || selected.service_type}
                  </DetailRow>

                  <div className="mt-6 border-t border-white/10 pt-5">
                    <div className="overline text-white/40 mb-2">Message</div>
                    <p className="text-white/80 leading-relaxed whitespace-pre-wrap text-sm">
                      {selected.message}
                    </p>
                  </div>

                  <div className="mt-6 border-t border-white/10 pt-5">
                    <div className="overline text-white/40 mb-2">Status</div>
                    <Select
                      value={selected.status}
                      onValueChange={(v) => changeStatus(selected.id, v)}
                    >
                      <SelectTrigger
                        data-testid="detail-status"
                        className="bg-[#05080F] border-white/10 text-white rounded-none h-11"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
                        {STATUS.map((s) => (
                          <SelectItem key={s.value} value={s.value} data-testid={`detail-status-${s.value}`}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-white/10 bg-[#0D1322]/50 p-10 text-center">
                  <span className="overline text-white/40">Select an inquiry</span>
                  <p className="text-white/50 text-sm mt-3">
                    Click a row to view details, change status, or reply.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      )}
    </main>
  );
}

const TabBtn = ({ active, onClick, label, icon, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
      active
        ? "bg-[#E5A93B] text-black"
        : "text-white/60 hover:text-white hover:bg-white/5"
    }`}
  >
    {icon}
    {label}
  </button>
);

const Stat = ({ icon, label, value, testid }) => (
  <div className="bg-[#0D1322] p-5 md:p-6" data-testid={testid}>
    <div className="flex items-center gap-2 overline text-white/40">
      <span className="text-[#E5A93B]">{icon}</span>
      {label}
    </div>
    <div className="mt-3 text-3xl md:text-4xl font-bold text-white font-mono">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-5 py-4 align-top ${className}`}>{children}</td>
);

const DetailRow = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-2">
    <div className="overline text-white/40 w-28 shrink-0 flex items-center gap-2 pt-0.5">
      {icon && <span className="text-[#E5A93B]">{icon}</span>}
      {label}
    </div>
    <div className="text-white text-sm flex-1 break-words">{children}</div>
  </div>
);
